import BasePriceTable from "@/components/base-price/BasePriceTable";
import Breadcrumb from "@/components/Breadcrumb";
import {
  useCreateNewQuarterBasePrices,
  useGetBasePrices,
  useUpdateBasePrices,
} from "@/hooks/basePriceHooks";
import { useGetAllMaterialTypes } from "@/hooks/materialTypeHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import {
  Box,
  MenuItem,
  Select,
  Tabs,
  Tab,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  DefaultSecondaryButton,
  NormalSecondaryButton,
  DefaultPrimaryButton,
} from "@/styles/globalstyles";
import EditIcon from "@mui/icons-material/Edit";
import { getAxiosErrorMessage } from "@/utils/healper";
import { Add } from "@mui/icons-material";
import {
  getCurrentQuarter,
  getUpcomingQuarter,
  getUpcomingQuarterYear,
} from "@/utils/date";
import dayjs from "dayjs";
import { usePermissions } from "@/hooks/usePermissions";
import {
  SuccessAlert,
  ErrorAlert,
  ConfirmAlert,
  WarningAlert,
} from "@/utils/alert";

const MaterialBasePriceManagement = () => {
  const canEdit = usePermissions().canEdit("baseprice");
  const [materialTypeId, setMaterialTypeId] = useState<number>(1);
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null);
  const [year, setYear] = useState<number>(dayjs().year());
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isFlagged, setIsFlagged] = useState<boolean>(false);
  const [editData, setEditData] = useState<
    Array<Record<string, string | number>>
  >([]);

  useEffect(() => {
    document.title = "Material Base Price | AMPA";
  }, []);

  const handleChangeQuarterTab = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedQuarter(newValue);
  };

  const {
    data: materialTypes,
    isFetching: materialTypeIsLoading,
    isError: materialTypeIsError,
  } = useGetAllMaterialTypes({});

  const {
    data: basePrices,
    isFetching: basePriceIsLoading,
    isError: basePriceIsError,
  } = useGetBasePrices({
    material_type_id: materialTypeId,
    year: year,
  });

  const { mutate: updateBasePrices, isPending: updateBasePricesIsLoading } =
    useUpdateBasePrices();

  const {
    mutate: createNewQuarterBasePrices,
    isPending: createNewQuarterBasePricesIsLoading,
  } = useCreateNewQuarterBasePrices();

  useQueryHandler({
    isFetching:
      basePriceIsLoading ||
      materialTypeIsLoading ||
      updateBasePricesIsLoading ||
      createNewQuarterBasePricesIsLoading,
    isError: basePriceIsError || materialTypeIsError,
    errorMessage: "ไม่สามารถโหลดข้อมูล Base Price ได้ กรุณาลองใหม่อีกครั้ง",
  });

  const allQuarters: number[] = useMemo(() => {
    return basePrices?.base_prices?.map((price) => price.quarter) || [];
  }, [basePrices]);

  const maxQuarter = allQuarters.length > 0 ? Math.max(...allQuarters) : 0;
  const quarter = selectedQuarter ?? maxQuarter;
  const shouldAddQuarter =
    year === getUpcomingQuarterYear() &&
    (maxQuarter === 0 ||
      (basePrices?.suppliers?.length && basePrices.suppliers.length > 0)) &&
    Boolean(basePrices?.specs?.length) &&
    (maxQuarter === 0 || maxQuarter < getUpcomingQuarter());

  useEffect(() => {
    setSelectedQuarter(null);
  }, [year, materialTypeId]);

  const tableData: Array<Record<string, string | number>> = useMemo(() => {
    if (!quarter) return [];
    return (
      basePrices?.suppliers?.map((supplier) => {
        const rowData: Record<string, string | number> = {
          supplier_name: supplier.name_abbr,
          supplier_id: supplier.id,
        };

        basePrices?.specs?.forEach((spec) => {
          const quarterPrices = basePrices?.base_prices?.find(
            (price) => price.quarter === quarter,
          );
          const priceData = quarterPrices?.data?.find(
            (price) =>
              price.material_supplier_id === supplier.id &&
              price.material_spec_id === spec.id,
          );
          rowData[spec.spec_name] = priceData?.base_price || "";
        });
        return rowData;
      }) || []
    );
  }, [basePrices, quarter]);

  const handleFlagCheckboxChange = (checked: boolean) => {
    if (checked) {
      WarningAlert(
        "Warning",
        "การบันทึกด้วยตัวเลือกนี้จะทำให้ Part ที่เกี่ยวข้องถูกติด Flagged สำหรับ Buyer ซึ่งต้องให้หัวหน้า Buyer เป็นผู้ปลด Flag เท่านั้น",
      );
    }
    setIsFlagged(checked);
  };

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setEditData(tableData);
  }, [tableData]);

  const handleAddQuarter = () => {
    ConfirmAlert(
      "ยืนยันการเพิ่ม Quarter",
      `คุณต้องการเพิ่ม Base Price สำหรับ Q${getUpcomingQuarter()} ปี ${getUpcomingQuarterYear()} หรือไม่?`,
      "ยืนยัน",
      "ยกเลิก",
    ).then((result) => {
      if (result) {
        proceedAddQuarter();
      }
    });
  };

  const proceedAddQuarter = () => {
    createNewQuarterBasePrices(
      {
        material_type_id: materialTypeId,
        target_quarter: getUpcomingQuarter(),
        target_year: getUpcomingQuarterYear(),
      },
      {
        onSuccess: () => {
          SuccessAlert("เพิ่ม Quarter สำเร็จ", "");
          setSelectedQuarter(null);
        },
        onError: (error) => {
          ErrorAlert("เพิ่ม Quarter ไม่สำเร็จ", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleSave = () => {
    const changedPrices: Array<{
      material_base_price_id: number;
      base_price: number;
    }> = [];

    editData?.forEach((editRow, index) => {
      const originalRow = tableData[index];
      const supplier_id = editRow["supplier_id"];

      basePrices?.specs?.forEach((spec) => {
        const editValue = editRow[spec.spec_name];
        const originalValue = originalRow[spec.spec_name];

        if (editValue !== originalValue) {
          const quarterPrices = basePrices?.base_prices?.find(
            (price) => price.quarter === quarter,
          );
          const priceData = quarterPrices?.data?.find(
            (price) =>
              price.material_supplier_id === supplier_id &&
              price.material_spec_id === spec.id,
          );

          if (priceData?.material_base_price_id) {
            changedPrices.push({
              material_base_price_id: priceData.material_base_price_id,
              base_price: parseFloat(editValue as string) || 0,
            });
          }
        }
      });
    });

    if (changedPrices.length > 0) {
      updateBasePrices(
        {
          prices: changedPrices,
          is_buyer_flagged: isFlagged,
        },
        {
          onSuccess: () => {
            SuccessAlert("แก้ไข Base Price สำเร็จ", "");
            setSelectedQuarter(null);
            setIsFlagged(false);
          },
          onError: (error) => {
            ErrorAlert(
              "แก้ไข Base Price ไม่สำเร็จ",
              getAxiosErrorMessage(error),
            );
          },
        },
      );
    }

    setIsEditing(false);
    setIsFlagged(false);
  };

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditData([]);
    setIsFlagged(false);
  }, []);

  const isPastQuarter =
    year < dayjs().year() ||
    (year === dayjs().year() && quarter < getCurrentQuarter());

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="baseprice" />
      <Box mt={1}>
        <Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          size="small"
        >
          <MenuItem key={dayjs().year() - 1} value={dayjs().year() - 1}>
            {dayjs().year() - 1}
          </MenuItem>
          <MenuItem key={dayjs().year()} value={dayjs().year()}>
            {dayjs().year()}
          </MenuItem>
          {dayjs().month() === 11 && (
            <MenuItem key={dayjs().year() + 1} value={dayjs().year() + 1}>
              {dayjs().year() + 1}
            </MenuItem>
          )}
        </Select>
        <Box
          mt={2}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: "9px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f5f5f5",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#d0d0d0",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "#b0b0b0",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              minWidth: "max-content",
              gap: 0,
            }}
          >
            {materialTypes?.map((tab) => (
              <Box
                key={tab.id}
                onClick={() => {
                  if (!isEditing) {
                    setMaterialTypeId(tab.id);
                  }
                }}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: "pointer",
                  borderBottom:
                    materialTypeId === tab.id
                      ? "2px solid #1976d2"
                      : "2px solid transparent",
                  backgroundColor:
                    materialTypeId === tab.id ? "#f0f7ff" : "transparent",
                  color: materialTypeId === tab.id ? "#1976d2" : "#666",
                  fontWeight: materialTypeId === tab.id ? 600 : 400,
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor:
                      materialTypeId === tab.id ? "#f0f7ff" : "#f5f5f5",
                    color: materialTypeId === tab.id ? "#1976d2" : "#333",
                  },
                }}
              >
                {tab.material_type_name}
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          mt={1}
          display="flex"
          justifyContent="space-between"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Tabs
              value={quarter}
              onChange={handleChangeQuarterTab}
              aria-label="tabs"
            >
              {allQuarters?.length > 0 &&
                allQuarters.map((q) => (
                  <Tab key={q} label={`Q${q}`} value={q} disabled={isEditing} />
                ))}
            </Tabs>
            {shouldAddQuarter && (
              <Button
                variant="contained"
                size="small"
                onClick={handleAddQuarter}
                disabled={!canEdit}
                sx={{ ...DefaultPrimaryButton, my: 1, fontSize: "0.75rem" }}
              >
                <Add /> เพิ่ม Quarter
              </Button>
            )}
          </Box>
          {!isEditing && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleEdit}
              disabled={
                !canEdit ||
                quarter <= 0 ||
                quarter !== maxQuarter ||
                isPastQuarter
              }
              sx={{ ...DefaultSecondaryButton, my: 1, fontSize: "0.875rem" }}
            >
              <EditIcon /> แก้ไข Base Price
            </Button>
          )}
          {isEditing && (
            <Box sx={{ my: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFlagged}
                    onChange={(e) => handleFlagCheckboxChange(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label="ต้องการให้ติด Flagged สำหรับ Buyer"
                sx={{ mr: 1 }}
              />
              <Button
                variant="outlined"
                sx={{ ...NormalSecondaryButton, fontSize: "0.875rem" }}
                onClick={handleCancel}
              >
                ยกเลิก
              </Button>
              <Button
                variant="outlined"
                sx={{ ...DefaultPrimaryButton, fontSize: "0.875rem" }}
                onClick={handleSave}
              >
                บันทึก
              </Button>
            </Box>
          )}
        </Box>
        <BasePriceTable
          columns={basePrices?.specs?.map((spec) => spec.spec_name) || []}
          data={tableData}
          isEditing={isEditing}
          editData={editData}
          setEditData={setEditData}
        />
      </Box>
    </Box>
  );
};

export default MaterialBasePriceManagement;
